import { useEffect, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Link } from "react-router";
import { ChevronDown, ChevronRight, Loader2, MessageSquareText, RefreshCw } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { SearchInput } from "@/components/ui/search-input";
import { useToast } from "@/hooks/use-toast";
import { formatDateTime } from "@/utils/dateUtils";
import { whatsappApi } from "@/services/whatsapp-api";
import type { WhatsAppAguardando, WhatsAppResumoListado } from "@/types/whatsapp";
import { WhatsAppAguardandoBadge } from "./WhatsAppBadges";
import { WhatsAppBuscaMensagens } from "./WhatsAppBuscaMensagens";
import { WhatsAppHipotese } from "./WhatsAppHipotese";
import { WhatsAppLinhaDoTempo } from "./WhatsAppLinhaDoTempo";
import { WhatsAppResumoAcoes } from "./WhatsAppResumoAcoes";
import { WhatsAppTranscriptDialog } from "./WhatsAppTranscriptDialog";
import { toUserError } from "@/lib/api-error";

const PAGINA = 50;

const FILTROS: { valor: WhatsAppAguardando | "todos"; label: string }[] = [
  { valor: "todos", label: "Todas" },
  { valor: "equipe", label: "Aguardando a equipe" },
  { valor: "contato", label: "Aguardando o contato" },
  { valor: "ninguem", label: "Sem pendência" },
];

function LinhaDoResumo({
  resumo,
  onVerConversa,
}: {
  resumo: WhatsAppResumoListado;
  onVerConversa: (conversationId: string) => void;
}) {
  const [aberto, setAberto] = useState(false);
  const ChevronIcon = aberto ? ChevronDown : ChevronRight;
  const mensagensNovas =
    !!resumo.ultima_mensagem_em &&
    new Date(resumo.ultima_mensagem_em) > new Date(resumo.mensagens_ate);

  return (
    <div className="space-y-1.5 px-4 py-3 text-sm">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <span className="w-28 shrink-0 text-xs text-muted-foreground">
          {formatDateTime(resumo.mensagens_ate)}
        </span>
        <span className="min-w-0 shrink-0 font-medium">
          {resumo.freelancer_id ? (
            <Link
              to={`/freelancers/${resumo.freelancer_id}`}
              className="text-primary hover:underline"
            >
              {resumo.freelancer_nome ?? "Freelancer"}
            </Link>
          ) : (
            (resumo.contact_name ?? resumo.telefone ?? "Contato")
          )}
        </span>
        {resumo.aguardando && (
          <WhatsAppAguardandoBadge aguardando={resumo.aguardando} desde={resumo.mensagens_ate} />
        )}
        {mensagensNovas && (
          <span className="text-xs font-medium text-amber-700">
            mensagens novas depois do resumo
          </span>
        )}
        <span className="ml-auto flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            className="h-7 gap-1 px-2 text-muted-foreground"
            onClick={() => setAberto((valor) => !valor)}
          >
            <ChevronIcon className="h-3.5 w-3.5" />
            {aberto ? "Esconder histórico" : "Ver histórico"}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 gap-1.5 px-2 text-muted-foreground"
            onClick={() => onVerConversa(resumo.conversation_id)}
            title="Ver conversa"
          >
            <MessageSquareText className="h-3.5 w-3.5" />
          </Button>
        </span>
      </div>
      {resumo.ultimo_status && <p className="text-muted-foreground">{resumo.ultimo_status}</p>}
      <WhatsAppHipotese resumo={resumo} />
      {aberto && (
        <div className="space-y-2 rounded-md border bg-muted/20 p-3">
          <WhatsAppLinhaDoTempo historico={resumo.historico} resumo={resumo.resumo} />
          <p className="text-xs text-muted-foreground">
            Resumo gerado em {formatDateTime(resumo.gerado_em)}
            {resumo.ultima_mensagem_contato_em &&
              ` · última mensagem do contato em ${formatDateTime(resumo.ultima_mensagem_contato_em)}`}
          </p>
          <WhatsAppResumoAcoes conversa={resumo} surface="aba_resumos" />
        </div>
      )}
    </div>
  );
}

/**
 * Resumo vigente de cada conversa, da conversa com atividade mais recente para
 * a mais antiga. A ordem é pela data da última mensagem que o resumo leu, que
 * fica gravada com ele; reprocessar não embaralha a lista.
 */
function RegerarResumos() {
  const { toast } = useToast();
  const regerar = useMutation({
    mutationFn: () => whatsappApi.regerarResumos(),
    onSuccess: ({ conversas }) =>
      toast({
        title: "Refazendo os resumos",
        description: `${conversas} conversas, leva alguns minutos. Recarregue a aba depois.`,
      }),
    onError: (error: Error) =>
      toast({
        title: "Não deu certo",
        description: toUserError(error).description,
        variant: "destructive",
      }),
  });
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button variant="outline" size="sm" disabled={regerar.isPending}>
          {regerar.isPending ? (
            <Loader2 className="mr-1 h-4 w-4 animate-spin" />
          ) : (
            <RefreshCw className="mr-1 h-4 w-4" />
          )}
          Refazer resumos
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Refazer todos os resumos?</AlertDialogTitle>
          <AlertDialogDescription>
            A IA relê cada conversa do começo e monta o histórico dia a dia de novo. Leva alguns
            minutos e tem custo de IA. A versão atual de cada resumo fica guardada.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancelar</AlertDialogCancel>
          <AlertDialogAction onClick={() => regerar.mutate()}>Refazer</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export function WhatsAppResumos() {
  const [filtro, setFiltro] = useState<WhatsAppAguardando | "todos">("todos");
  const [limite, setLimite] = useState(PAGINA);
  const [conversaAberta, setConversaAberta] = useState<string | null>(null);
  const [mensagemDestacada, setMensagemDestacada] = useState<string | null>(null);
  const [busca, setBusca] = useState("");
  const [termo, setTermo] = useState("");

  useEffect(() => {
    const espera = setTimeout(() => setTermo(busca.trim()), 350);
    return () => clearTimeout(espera);
  }, [busca]);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["whatsapp-resumos", filtro, limite],
    queryFn: () =>
      whatsappApi.listResumos({
        aguardando: filtro === "todos" ? undefined : filtro,
        limit: limite,
      }),
    placeholderData: (anterior) => anterior,
  });

  const resumos = data?.resumos ?? [];
  const contagem = data?.por_aguardando ?? {};
  const totalDoFiltro = filtro === "todos" ? (data?.total ?? 0) : (contagem[filtro] ?? 0);

  const abrirConversa = (conversationId: string, messageId: string | null = null) => {
    setMensagemDestacada(messageId);
    setConversaAberta(conversationId);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <SearchInput
          value={busca}
          onChange={setBusca}
          placeholder="Buscar nas mensagens"
          className="w-full sm:w-72"
        />
        <div className="ml-auto">
          <RegerarResumos />
        </div>
      </div>

      {termo ? (
        <WhatsAppBuscaMensagens termo={termo} onVerNoContexto={abrirConversa} />
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-2">
            {FILTROS.map((opcao) => (
              <Button
                key={opcao.valor}
                variant={filtro === opcao.valor ? "default" : "outline"}
                size="sm"
                onClick={() => {
                  setFiltro(opcao.valor);
                  setLimite(PAGINA);
                }}
              >
                {opcao.label}
                {opcao.valor === "todos"
                  ? ` (${data?.total ?? 0})`
                  : ` (${contagem[opcao.valor] ?? 0})`}
              </Button>
            ))}
          </div>

          {isLoading ? (
            <div className="flex justify-center py-10">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : resumos.length === 0 ? (
            <div className="py-10 text-center text-sm text-muted-foreground">
              Nenhum resumo ainda. Eles nascem a cada processamento.
            </div>
          ) : (
            <Card>
              <CardContent className="divide-y p-0">
                {resumos.map((resumo) => (
                  <LinhaDoResumo
                    key={resumo.conversation_id}
                    resumo={resumo}
                    onVerConversa={abrirConversa}
                  />
                ))}
              </CardContent>
            </Card>
          )}

          {resumos.length < totalDoFiltro && (
            <div className="flex justify-center">
              <Button
                variant="outline"
                size="sm"
                disabled={isFetching}
                onClick={() => setLimite((valor) => valor + PAGINA)}
              >
                {isFetching ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}
                Mostrar mais ({totalDoFiltro - resumos.length} restantes)
              </Button>
            </div>
          )}
        </>
      )}

      <WhatsAppTranscriptDialog
        conversationId={conversaAberta}
        highlightIds={mensagemDestacada ? [mensagemDestacada] : []}
        onClose={() => setConversaAberta(null)}
      />
    </div>
  );
}
